/**
 * Pipeline Help Page - Learning Guide
 *
 * Comprehensive guide to understanding and using the pipeline system
 */

import { ErrorBoundary } from './components'
import { SettingsIcon, CheckIcon } from '@bizfirst/common-react'

export function PipelineHelpPage() {
  return (
    <ErrorBoundary>
      <div className="page-container hooks-subscribers-page">
        <div className="page-header">
          <h1 style={{ display: 'flex', alignItems: 'center', gap: '10px' }}><SettingsIcon /> Understanding Pipelines</h1>
        <p className="page-description">
          Learn how pipelines route messages, compose hooks, and define message flow through EdgeStream
        </p>
      </div>

      {/* What Are Pipelines */}
      <section className="section">
        <h2>What Are Pipelines?</h2>
        <p>
          Pipelines are configurations that define how messages flow through EdgeStream. They specify routing rules, hook
          execution, and subscriber configuration for specific topics or patterns.
        </p>
        <div style={{ background: 'rgba(15, 23, 42, 0.95)', border: '1px solid rgba(58, 140, 69, 0.4)', padding: 'var(--spacing-lg)', borderRadius: '12px', marginTop: 'var(--spacing-md)' }}>
          <code style={{ display: 'block', color: '#a8d8ea', lineHeight: '1.6' }}>
            Pipeline incoming message on topic → Execute hooks → Route to subscribers
          </code>
        </div>
      </section>

      {/* Pipeline Types */}
      <section className="section">
        <h2>Pipeline Types</h2>
        <p>EdgeStream supports different pipeline directions for different use cases:</p>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 'var(--spacing-lg)', marginTop: 'var(--spacing-md)' }}>
          <div style={{ background: 'rgba(15, 23, 42, 0.95)', border: '1px solid rgba(58, 140, 69, 0.3)', padding: 'var(--spacing-lg)', borderRadius: '8px', borderLeft: '4px solid #1d4ed8' }}>
            <h4 style={{ margin: '0 0 var(--spacing-md) 0', color: '#1d4ed8', display: 'flex', alignItems: 'center', gap: '6px' }}>↓ Incoming</h4>
            <p style={{ margin: '0 0 var(--spacing-sm) 0', color: 'var(--text-secondary)' }}>Processes messages received from external systems</p>
            <code style={{ background: 'var(--bg-dark)', padding: '8px', borderRadius: '4px', color: '#a8d8ea', fontSize: '12px', display: 'block' }}>
              Server → Hooks → Subscribers
            </code>
          </div>

          <div style={{ background: 'rgba(15, 23, 42, 0.95)', border: '1px solid rgba(58, 140, 69, 0.3)', padding: 'var(--spacing-lg)', borderRadius: '8px', borderLeft: '4px solid #16a34a' }}>
            <h4 style={{ margin: '0 0 var(--spacing-md) 0', color: '#16a34a', display: 'flex', alignItems: 'center', gap: '6px' }}>↑ Outgoing</h4>
            <p style={{ margin: '0 0 var(--spacing-sm) 0', color: 'var(--text-secondary)' }}>Processes messages being sent to external systems</p>
            <code style={{ background: 'var(--bg-dark)', padding: '8px', borderRadius: '4px', color: '#a8d8ea', fontSize: '12px', display: 'block' }}>
              App → Hooks → Server
            </code>
          </div>

          <div style={{ background: 'rgba(15, 23, 42, 0.95)', border: '1px solid rgba(58, 140, 69, 0.3)', padding: 'var(--spacing-lg)', borderRadius: '8px', borderLeft: '4px solid #d97706' }}>
            <h4 style={{ margin: '0 0 var(--spacing-md) 0', color: '#d97706', display: 'flex', alignItems: 'center', gap: '6px' }}>↔ Bidirectional</h4>
            <p style={{ margin: '0 0 var(--spacing-sm) 0', color: 'var(--text-secondary)' }}>Handles both incoming and outgoing messages</p>
            <code style={{ background: 'var(--bg-dark)', padding: '8px', borderRadius: '4px', color: '#a8d8ea', fontSize: '12px', display: 'block' }}>
              Both directions supported
            </code>
          </div>
        </div>
      </section>

      {/* Pipeline Components */}
      <section className="section">
        <h2>Pipeline Components</h2>

        <div style={{ marginTop: 'var(--spacing-md)' }}>
          <h3 style={{ fontSize: '16px', color: 'var(--color-primary)', marginBottom: 'var(--spacing-md)' }}>1. Metadata</h3>
          <p style={{ color: 'var(--text-secondary)', marginBottom: 'var(--spacing-md)' }}>
            Describes the pipeline (name, ID, description, direction, impact level)
          </p>

          <h3 style={{ fontSize: '16px', color: 'var(--color-primary)', marginBottom: 'var(--spacing-md)' }}>2. Hooks</h3>
          <p style={{ color: 'var(--text-secondary)', marginBottom: 'var(--spacing-md)' }}>
            Composable stages that process messages (logging, filtering, validation, transformation)
          </p>

          <h3 style={{ fontSize: '16px', color: 'var(--color-primary)', marginBottom: 'var(--spacing-md)' }}>3. Subscribers</h3>
          <p style={{ color: 'var(--text-secondary)', marginBottom: 'var(--spacing-md)' }}>
            Handlers that listen for messages matching topic patterns
          </p>
        </div>
      </section>

      {/* Hook Execution */}
      <section className="section">
        <h2>Hook Execution Order</h2>
        <p>Hooks execute in priority order, allowing early termination of the pipeline:</p>

        <div style={{ marginTop: 'var(--spacing-md)' }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-md)' }}>
            <div style={{ display: 'flex', gap: 'var(--spacing-lg)', alignItems: 'flex-start' }}>
              <div
                style={{
                  background: 'var(--color-primary)',
                  color: 'white',
                  width: '40px',
                  height: '40px',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontWeight: 'bold',
                  flexShrink: 0,
                }}
              >
                1
              </div>
              <div>
                <h4 style={{ margin: '0 0 var(--spacing-xs) 0' }}>Message Received</h4>
                <p style={{ margin: 0, color: 'var(--text-secondary)', fontSize: '14px' }}>Incoming message from server</p>
              </div>
            </div>

            <div style={{ display: 'flex', gap: 'var(--spacing-lg)', alignItems: 'flex-start' }}>
              <div
                style={{
                  background: 'var(--color-primary)',
                  color: 'white',
                  width: '40px',
                  height: '40px',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontWeight: 'bold',
                  flexShrink: 0,
                }}
              >
                2
              </div>
              <div>
                <h4 style={{ margin: '0 0 var(--spacing-xs) 0' }}>Execute Hooks</h4>
                <p style={{ margin: 0, color: 'var(--text-secondary)', fontSize: '14px' }}>Hooks run in priority order (low → high)</p>
              </div>
            </div>

            <div style={{ display: 'flex', gap: 'var(--spacing-lg)', alignItems: 'flex-start' }}>
              <div
                style={{
                  background: 'var(--color-primary)',
                  color: 'white',
                  width: '40px',
                  height: '40px',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontWeight: 'bold',
                  flexShrink: 0,
                }}
              >
                3
              </div>
              <div>
                <h4 style={{ margin: '0 0 var(--spacing-xs) 0' }}>Route to Subscribers</h4>
                <p style={{ margin: 0, color: 'var(--text-secondary)', fontSize: '14px' }}>Send to matching topic subscribers</p>
              </div>
            </div>

            <div style={{ display: 'flex', gap: 'var(--spacing-lg)', alignItems: 'flex-start' }}>
              <div
                style={{
                  background: 'var(--color-primary)',
                  color: 'white',
                  width: '40px',
                  height: '40px',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontWeight: 'bold',
                  flexShrink: 0,
                }}
              >
                <CheckIcon />
              </div>
              <div>
                <h4 style={{ margin: '0 0 var(--spacing-xs) 0' }}>Complete</h4>
                <p style={{ margin: 0, color: 'var(--text-secondary)', fontSize: '14px' }}>Pipeline execution finished</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Impact Levels */}
      <section className="section">
        <h2>Impact Levels</h2>
        <p>Pipelines are classified by their impact on system operation:</p>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 'var(--spacing-lg)', marginTop: 'var(--spacing-md)' }}>
          <div style={{ background: 'rgba(15, 23, 42, 0.95)', border: '1px solid rgba(58, 140, 69, 0.3)', padding: 'var(--spacing-lg)', borderRadius: '8px', borderLeft: '4px solid #dc2626' }}>
            <h4 style={{ margin: '0 0 var(--spacing-md) 0', color: '#dc2626', display: 'flex', alignItems: 'center', gap: '6px' }}><span style={{ width: 12, height: 12, borderRadius: '50%', background: '#dc2626', display: 'inline-block', flexShrink: 0 }} /> Critical</h4>
            <p style={{ margin: 0, color: 'var(--text-secondary)', fontSize: '14px' }}>System failure if pipeline fails</p>
          </div>

          <div style={{ background: 'rgba(15, 23, 42, 0.95)', border: '1px solid rgba(58, 140, 69, 0.3)', padding: 'var(--spacing-lg)', borderRadius: '8px', borderLeft: '4px solid #ea580c' }}>
            <h4 style={{ margin: '0 0 var(--spacing-md) 0', color: '#ea580c', display: 'flex', alignItems: 'center', gap: '6px' }}><span style={{ width: 12, height: 12, borderRadius: '50%', background: '#ea580c', display: 'inline-block', flexShrink: 0 }} /> High</h4>
            <p style={{ margin: 0, color: 'var(--text-secondary)', fontSize: '14px' }}>Major features affected</p>
          </div>

          <div style={{ background: 'rgba(15, 23, 42, 0.95)', border: '1px solid rgba(58, 140, 69, 0.3)', padding: 'var(--spacing-lg)', borderRadius: '8px', borderLeft: '4px solid #ca8a04' }}>
            <h4 style={{ margin: '0 0 var(--spacing-md) 0', color: '#ca8a04', display: 'flex', alignItems: 'center', gap: '6px' }}><span style={{ width: 12, height: 12, borderRadius: '50%', background: '#ca8a04', display: 'inline-block', flexShrink: 0 }} /> Medium</h4>
            <p style={{ margin: 0, color: 'var(--text-secondary)', fontSize: '14px' }}>Some features degraded</p>
          </div>

          <div style={{ background: 'rgba(15, 23, 42, 0.95)', border: '1px solid rgba(58, 140, 69, 0.3)', padding: 'var(--spacing-lg)', borderRadius: '8px', borderLeft: '4px solid #16a34a' }}>
            <h4 style={{ margin: '0 0 var(--spacing-md) 0', color: '#16a34a', display: 'flex', alignItems: 'center', gap: '6px' }}><span style={{ width: 12, height: 12, borderRadius: '50%', background: '#16a34a', display: 'inline-block', flexShrink: 0 }} /> Low</h4>
            <p style={{ margin: 0, color: 'var(--text-secondary)', fontSize: '14px' }}>Minor or no impact</p>
          </div>
        </div>
      </section>

      {/* Best Practices */}
      <section className="section">
        <h2>Best Practices</h2>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 'var(--spacing-lg)', marginTop: 'var(--spacing-md)' }}>
          <div style={{ background: 'rgba(15, 23, 42, 0.95)', border: '1px solid rgba(58, 140, 69, 0.3)', borderLeft: '4px solid var(--obs-success)', padding: 'var(--spacing-lg)', borderRadius: '4px' }}>
            <h4 style={{ margin: '0 0 var(--spacing-sm) 0', color: 'var(--obs-success)' }}>Keep Hooks Focused</h4>
            <p style={{ margin: 0, color: 'var(--text-secondary)', fontSize: '14px' }}>Each hook should have a single responsibility</p>
          </div>

          <div style={{ background: 'rgba(15, 23, 42, 0.95)', border: '1px solid rgba(58, 140, 69, 0.3)', borderLeft: '4px solid var(--obs-success)', padding: 'var(--spacing-lg)', borderRadius: '4px' }}>
            <h4 style={{ margin: '0 0 var(--spacing-sm) 0', color: 'var(--obs-success)' }}>Order Matters</h4>
            <p style={{ margin: 0, color: 'var(--text-secondary)', fontSize: '14px' }}>Hooks execute in priority order - plan accordingly</p>
          </div>

          <div style={{ background: 'rgba(15, 23, 42, 0.95)', border: '1px solid rgba(58, 140, 69, 0.3)', borderLeft: '4px solid var(--obs-success)', padding: 'var(--spacing-lg)', borderRadius: '4px' }}>
            <h4 style={{ margin: '0 0 var(--spacing-sm) 0', color: 'var(--obs-success)' }}>Handle Errors</h4>
            <p style={{ margin: 0, color: 'var(--text-secondary)', fontSize: '14px' }}>Implement error handling in hooks to prevent pipeline failure</p>
          </div>

          <div style={{ background: 'rgba(15, 23, 42, 0.95)', border: '1px solid rgba(58, 140, 69, 0.3)', borderLeft: '4px solid var(--obs-warning)', padding: 'var(--spacing-lg)', borderRadius: '4px' }}>
            <h4 style={{ margin: '0 0 var(--spacing-sm) 0', color: 'var(--obs-warning)' }}>Avoid Blocking</h4>
            <p style={{ margin: 0, color: 'var(--text-secondary)', fontSize: '14px' }}>Don't perform heavy operations in hooks - offload to workers</p>
          </div>

          <div style={{ background: 'rgba(15, 23, 42, 0.95)', border: '1px solid rgba(58, 140, 69, 0.3)', borderLeft: '4px solid var(--obs-warning)', padding: 'var(--spacing-lg)', borderRadius: '4px' }}>
            <h4 style={{ margin: '0 0 var(--spacing-sm) 0', color: 'var(--obs-warning)' }}>Monitor Performance</h4>
            <p style={{ margin: 0, color: 'var(--text-secondary)', fontSize: '14px' }}>Track hook execution time to identify bottlenecks</p>
          </div>

          <div style={{ background: 'rgba(15, 23, 42, 0.95)', border: '1px solid rgba(58, 140, 69, 0.3)', borderLeft: '4px solid var(--obs-warning)', padding: 'var(--spacing-lg)', borderRadius: '4px' }}>
            <h4 style={{ margin: '0 0 var(--spacing-sm) 0', color: 'var(--obs-warning)' }}>Test Thoroughly</h4>
            <p style={{ margin: 0, color: 'var(--text-secondary)', fontSize: '14px' }}>Test pipeline configurations with realistic data and loads</p>
          </div>
        </div>
      </section>

      {/* Real-World Example */}
      <section className="section">
        <h2>Real-World Example</h2>
        <p>A typical user creation pipeline:</p>

        <div style={{ background: 'rgba(15, 23, 42, 0.95)', border: '1px solid rgba(58, 140, 69, 0.4)', padding: 'var(--spacing-lg)', borderRadius: '12px', marginTop: 'var(--spacing-md)' }}>
          <code style={{ display: 'block', color: '#a8d8ea', lineHeight: '1.8', fontSize: '13px' }}>
            Message: user.created {'{id, email, name}'}<br />
            <br />
            1. LogHook → Log to console/file<br />
            2. ValidationHook → Validate email format<br />
            3. EncryptionHook → Encrypt sensitive data<br />
            4. SignMessageHook → Sign message<br />
            5. PublishToSubscribersHook → Send to listeners<br />
            <br />
            ✓ User creation event processed
          </code>
                </div>
        </section>
      </div>
    </ErrorBoundary>
  );
}
